import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../hooks/useAuth';
import { authSchema, type AuthFormData } from '@ext-bookmark/shared/auth';
import { FormError } from './ui/form-error';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const { signUp, signIn } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<AuthFormData>({
    resolver: zodResolver(authSchema),
    defaultValues: {
      email: '',
      password: '',
      username: '',
    },
  });

  const onSubmit = async (data: AuthFormData) => {
    setApiError(null);
    try {
      if (isSignUp) {
        await signUp(data.email, data.password, data.username);
      } else {
        await signIn(data.email, data.password);
      }
      reset();
      onClose();
    } catch (err: any) {
      setApiError(err.message || '操作失败，请重试');
    }
  };

  /** 校验未通过时提示，避免用户以为点击无效、没有发起请求 */
  const onInvalid = () => {
    setApiError('请填写正确的邮箱和密码（密码至少 6 位）');
  };

  const handleToggleMode = () => {
    setIsSignUp(!isSignUp);
    setApiError(null);
    reset();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isSignUp ? '注册' : '登录'}</DialogTitle>
          <DialogDescription>
            {isSignUp ? '创建新账号以开始使用' : '登录您的账号'}
          </DialogDescription>
        </DialogHeader>
        
        {apiError && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            {apiError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
          {isSignUp && (
            <div className="space-y-2">
              <Label htmlFor="username">用户名</Label>
              <Input
                id="username"
                type="text"
                {...register('username')}
                placeholder="请输入用户名"
              />
              <FormError error={errors.username?.message} />
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email">邮箱</Label>
            <Input
              id="email"
              type="email"
              {...register('email')}
              placeholder="请输入邮箱"
            />
            <FormError error={errors.email?.message} />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">密码</Label>
            <Input
              id="password"
              type="password"
              {...register('password')}
              placeholder="请输入密码"
            />
            <FormError error={errors.password?.message} />
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? '处理中...' : (isSignUp ? '注册' : '登录')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              取消
            </Button>
          </DialogFooter>
        </form>

        <div className="text-center">
          <button
            onClick={handleToggleMode}
            className="text-sm text-primary hover:underline"
          >
            {isSignUp ? '已有账号？去登录' : '没有账号？去注册'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
